import axios from "axios";
import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate, Route, Routes } from "react-router-dom";
import {
  Anchor,
  Button,
  Flex,
  Grid,
  Card,
  Space,
  Switch,
  Text,
} from "@mantine/core";
import { useSelector } from "react-redux";
import { IconCopy } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  ErrorView,
  PageTitle,
  Subnav,
  EntitySubtitles,
} from "@components/shared";
import {
  ContestAlerts,
  ContestAssets,
  ContestFinalists,
  ContestKeywordManagement,
  ContestParticipants,
  ContestReadOnly,
  ContestReplicate,
  ContestSettings,
  ContestStatus,
  ContestWizardStatus,
} from "@components/Contest";
import { Taggit } from "@components/Tag";
import { PrizeManagement } from "@components/Prize";
import { EntityEmailTemplates } from "@components/EmailServer";
import { PrizePoolForm } from "@components/PrizePool";
import { LocationAssociate } from "@components/Location";

import entityColor from "@util/entityColor";
import entityIcon from "@util/entityIcon";
import { penniesToDollars } from "@util/formatters";

export default function PrizePoolDetail() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [showWizard, setShowWizard] = useState(true);
  const params = useParams();
  const { id } = params;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const managerInfo = useSelector((state) => state.manager);
  const isManager = managerInfo ? true : false;

  useEffect(() => {
    setInfo(null);
    fetchData();
  }, [id]);

  function fetchData() {
    axios
      .get(`/contests/${params.id}/`)
      .then(({ data }) => {
        setInfo(data.response[0]);
      })
      .catch((err) => {
        setInfo(null);
        setError(err);
      });
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!info) return null;

  const { wizard_settings: wizardSettings, wizard_status: wizardStatus } = info;
  const wizardComplete = wizardStatus.wizard_complete;
  const showLocations = false;
  // const showLocations =
  //   (!managerInfo ||
  //     (managerInfo && managerInfo.organization_id) ||
  //     info.organization_id) &&
  //   info.created_in_campaign_effort_id;

  if (!info.edit) {
    return (
      <React.Fragment>
        <ContestTitle info={info} />
        <ContestReadOnly info={info} />
      </React.Fragment>
    );
  }

  if (!wizardComplete) {
    return (
      <div>
        <ContestTitle info={info} />
        {wizardComplete && (
          <WizardToggle showWizard={showWizard} setShowWizard={setShowWizard} />
        )}
        <ContestWizardStatus
          effortId={
            info.created_in_campaign_effort_id || info.campaign_effort_id
          }
          isPrizePool
          fetchData={fetchData}
          id={id}
          locationId={info.location_id}
          organizationId={info.organization_id}
          prize={info.prizes.length ? info.prizes[0] : null}
          status={info.status}
          wizardComplete={wizardComplete}
          wizardSettings={wizardSettings}
          wizardStatus={wizardStatus}
          contestDates={
            info.start_date && info.end_date
              ? {
                  start_date: info.start_date,
                  start_time: info.start_time,
                  start_date_formatted: info.start_date_formatted,
                  end_date: info.end_date,
                  end_date_formatted: info.end_date_formatted,
                  end_time: info.end_time,
                }
              : null
          }
        />
      </div>
    );
  }

  return (
    <div>
      <ContestTitle info={info} />
      <Text mb="sm" size="sm">
        This Prize Pool is{" "}
        <b>
          {info.status === 1 ? "waiting" : info.status === 2 ? "on" : "off"}
        </b>
      </Text>
      {/* {wizardComplete && <WizardToggle />} */}
      {/* <Space mt="lg" /> */}
      <Flex>
        <Subnav
          buttonColor={entityColor.contest}
          links={[
            { to: `/prize-pools/${id}`, text: t("common:detail") },
            // { to: `/prize-pools/${id}/keywords`, text: "Keywords" },
            // { to: `/prize-pools/${id}/settings`, text: "Settings" },
            // { to: `/prize-pools/${id}/participants`, text: "Participants" },
            { to: `/prize-pools/${id}/assets`, text: t("common:image") },
            // { to: `/prize-pools/${id}/tags`, text: "Tags" },
            { to: `/prize-pools/${id}/finalists`, text: "Finalists" },
            { to: `/prize-pools/${id}/prizes`, text: "Prize" },
            { to: `/prize-pools/${id}/email`, text: t("common:email") },
            {
              to: `/prize-pools/${id}/replication`,
              text: "Replication",
              visible: managerInfo ? true : false,
            },
            {
              to: `/efforts/${info.created_in_campaign_effort_id}/locations`,
              text: "Locations",
              visible:
                info.created_in_campaign_effort_id && info.organization_id,
              target: "_blank",
            },
          ].filter((f) => f.visible !== false)}
        />
      </Flex>
      <Space mt="lg" />
      {wizardComplete && (
        <ContestAlerts contestId={id} setShowWizard={(e) => setShowWizard(e)} />
      )}
      <Routes>
        <Route
          exact
          path="/"
          element={
            <React.Fragment>
              <Grid>
                <Grid.Col span={{ base: 12, md: 12 }}>
                  <Card>
                    <PrizePoolForm
                      allowLocationAccess={info.allow_location_access}
                      contactName={info.contact_name}
                      contactNumber={info.contact_number}
                      contactEmail={info.contact_email}
                      endDate={info.end_date}
                      endTime={info.end_time}
                      id={id}
                      isManager={isManager}
                      locationId={info.location_id}
                      name={info.name}
                      onSuccess={fetchData}
                      pickupDate={info.pickup_date}
                      pickupTime={info.pickup_time}
                      pickupTimezone={info.pickup_timezone}
                      retailValue={
                        info.retail_value_in_pennies
                          ? penniesToDollars(info.retail_value_in_pennies)
                          : ""
                      }
                      startDate={info.start_date}
                      startTime={info.start_time}
                      timezone={info.timezone}
                    />
                  </Card>
                </Grid.Col>
                {/* <Grid.Col span={{ base: 12, md: 3 }}>
                  <ContestStatus
                    contestId={id}
                    status={info.status}
                    fetchData={fetchData}
                    liveStatus={info.live_status}
                    isPrizePool
                  />
                </Grid.Col> */}
              </Grid>
            </React.Fragment>
          }
        />
        <Route
          path="keywords"
          element={
            <React.Fragment>
              <ContestKeywordManagement contestId={id} />
            </React.Fragment>
          }
        />
        <Route
          path="prizes"
          element={
            <React.Fragment>
              <PrizeManagement
                organizationId={info.organization_id}
                locationId={info.location_id}
                contestId={id}
                fetchData={fetchData}
                isPrizePool
                contestForDuplication={info.for_duplication}
              />
            </React.Fragment>
          }
        />
        {/* <Route
          path="participants"
          element={
            <React.Fragment>
              <ContestParticipants
                contestId={id}
                orgId={info.organization_id}
                locationId={info.location_id}
              />
            </React.Fragment>
          }
        /> */}
        {/* <Route
          path="settings"
          element={
            <React.Fragment>
              <ContestSettings
                contestId={id}
                beginDate={info.start_date}
                endDate={info.end_date}
                contestInfo={info}
                fetchContest={fetchData}
                canEditWinnerRules={info.can_edit_winner_rules}
              />
            </React.Fragment>
          }
        /> */}
        <Route
          path="assets"
          element={
            <React.Fragment>
              <ContestAssets
                contestId={id}
                contestInfo={info}
                fetchContest={fetchData}
              />
            </React.Fragment>
          }
        />
        <Route
          path="finalists"
          element={
            <React.Fragment>
              <ContestFinalists
                contestId={id}
                contestSettings={info.settings}
                fetchData={fetchData}
                totalEntrants={info.total_entrants}
              />
            </React.Fragment>
          }
        />
        <Route
          path="email"
          element={
            <React.Fragment>
              <EntityEmailTemplates
                postmarkTemplateNames={info.postmark_template_names}
                contestId={id}
                templates={[
                  {
                    name: "Winner Notification",
                    field_name: "winner-notification",
                    value: info.winner_template,
                  },
                ]}
                fetchData={fetchData}
              />
            </React.Fragment>
          }
        />
        {showLocations && (
          <Route
            exact
            path="locations"
            element={
              <React.Fragment>
                <LocationAssociate
                  effortId={info.created_in_campaign_effort_id}
                />
              </React.Fragment>
            }
          />
        )}
        {!managerInfo && (
          <Route
            path="replication"
            element={
              <>
                <ContestReplicate
                  contestId={id}
                  onSuccess={() => navigate(`/prize-pools/${id}`)}
                  disabled={!wizardComplete}
                />
              </>
            }
          />
        )}
      </Routes>
    </div>
  );
}

const ContestTitle = ({ info }) => {
  const { t } = useTranslation();
  const managerInfo = useSelector((state) => state.manager);

  return (
    <>
      <PageTitle
        title={info.name}
        subtitle={
          <React.Fragment>{t("mixer:prizepool_singular")}</React.Fragment>
        }
        icon={entityIcon.contest()}
        iconColor={entityColor.contest}
        style={{ marginBottom: 0 }}
      />
      {true && (
        <React.Fragment>
          <EntitySubtitles
            info={{
              ...info,
              campaign_effort_name: info.created_in_name,
              campaign_effort_id: info.created_in_campaign_effort_id,
              location_id: managerInfo ? null : info.location_id,
              organization_id: managerInfo ? null : info.organization_id,
            }}
            tags={<Taggit reqInfo={{ contest_id: info.id }} />}
          />
        </React.Fragment>
      )}
      {info.for_duplication && (
        <PageTitle
          icon={<IconCopy />}
          iconColor="gray"
          subtitle="For duplication purposes only"
        />
      )}
      <Space mt="lg" />
    </>
  );
};

const WizardToggle = ({ showWizard, setShowWizard }) => {
  const { t } = useTranslation();
  return (
    <Switch
      label={t("common:quick_settings")}
      checked={showWizard}
      onChange={() => setShowWizard(!showWizard)}
      mb="lg"
    />
  );
};
